<template>
  <div>
    <!-- コンテンツラッパー -->
    <div class="image-uploader-container">
      <div class="upload-form-label">
        <FormLabel :label="label" :isRequired="isRequired" />
      </div>
      <div class="image-uploader-main-container">
        <!-- 左側: ドロップゾーン -->
        <div class="left-section">
          <div class="drop-zone-wrapper">
            <div
              class="drop-zone"
              :class="{ 'drag-over': isDragOver }"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @drop.prevent="handleDrop"
            >
              <div class="drop-zone-container">
                <div class="upload-icon-container">
                  <feather-icon icon="UploadCloudIcon" svgClasses="w-10 h-10" />
                </div>
                <div class="drop-zone-text">
                  画像・PDFファイルをドロップ<br />
                  または<br />
                </div>
                <Button
                  @click="triggerFileInput"
                  label="ファイルを選択"
                  :buttonStyle="{
                    padding: '4px 8px',
                    width: 'auto !important',
                  }"
                ></Button>
                <input
                  type="file"
                  ref="fileInput"
                  multiple
                  :accept="acceptTypes.join(',')"
                  @change="handleFileSelect"
                  hidden
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 右側: 画像リスト -->
        <div class="right-section">
          <!-- ファイルリストコンテナ -->
          <div class="files-container">
            <!-- 画像がない場合のプレースホルダー表示 -->
            <div v-if="selectedFiles.length === 0" class="empty-state">
              <div class="empty-state-content">
                <div class="format-text">フォーマット：PNG、JPG、JPEG、GIF、PDF</div>
                <div class="format-text">
                  最大アップロードサイズ：{{ maxSizeMB }} MB
                </div>
              </div>
            </div>

            <!-- アップロード済みファイル -->
            <div
              v-if="completedFiles.length > 0"
              class="completed-files-section"
            >
              <TransitionGroup name="upload-list" tag="div" class="upload-list">
                <div
                  v-for="file in completedFiles"
                  :key="file.id"
                  class="upload-card completed"
                >
                  <!-- プレビュー画像 / PDF -->
                  <div class="file-preview">
                    <img
                      v-if="!isPdf(file) && file.previewUrl"
                      :src="file.previewUrl"
                      :alt="file.name"
                      class="preview-image"
                    />
                    <div
                      v-else-if="isPdf(file)"
                      class="preview-placeholder preview-pdf"
                      title="PDF"
                    >
                      <span class="preview-pdf-text">PDF</span>
                    </div>
                    <div v-else class="preview-placeholder">
                      <feather-icon icon="ImageIcon" svgClasses="w-6 h-6" />
                    </div>
                  </div>

                  <div class="upload-card-content">
                    <!-- ファイル名 -->
                    <div class="upload-card-row">
                      <div class="file-name-wrapper">
                        <div class="file-name">{{ file.name }}</div>
                      </div>
                    </div>

                    <!-- ステータスとアクション -->
                    <div class="upload-card-row-full">
                      <div class="upload-card-actions">
                        <button
                          class="action-icon cancel-icon"
                          @click="removeFileById(file.id)"
                          type="button"
                          title="削除"
                        >
                          <vs-icon icon="cancel" size="24px" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </TransitionGroup>
            </div>

            <!-- 未完了ファイル -->
            <div v-if="pendingFiles.length > 0" class="pending-files-section">
              <TransitionGroup name="upload-list" tag="div" class="upload-list">
                <div
                  v-for="file in pendingFiles"
                  :key="file.id"
                  class="upload-card"
                >
                  <!-- プレビュー画像 / PDF -->
                  <div class="file-preview">
                    <img
                      v-if="!isPdf(file) && file.previewUrl"
                      :src="file.previewUrl"
                      :alt="file.name"
                      class="preview-image"
                    />
                    <div
                      v-else-if="isPdf(file)"
                      class="preview-placeholder preview-pdf"
                      title="PDF"
                    >
                      <span class="preview-pdf-text">PDF</span>
                    </div>
                    <div v-else class="preview-placeholder">
                      <feather-icon icon="ImageIcon" svgClasses="w-6 h-6" />
                    </div>
                  </div>

                  <div class="upload-card-content">
                    <!-- ファイル名 -->
                    <div class="upload-card-row">
                      <div class="file-name-wrapper">
                        <div class="file-name">{{ file.name }}</div>
                      </div>
                    </div>

                    <!-- ステータスとアクション -->
                    <div class="upload-card-row-full">
                      <div class="upload-card-actions">
                        <button
                          class="action-icon cancel-icon"
                          @click="removeFileById(file.id)"
                          type="button"
                          title="キャンセル"
                        >
                          <vs-icon icon="cancel" size="24px" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </TransitionGroup>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- エラーメッセージ -->
    <div v-if="errorMessage" class="error-message">
      <feather-icon icon="AlertTriangleIcon" svgClasses="w-4 h-4" />
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
import Button from "../Button.vue";
import FormLabel from "./FormLabel.vue";

// 定数定義
const MAX_FILE_SIZE_MB = 5;
const ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/gif",
  "application/pdf",
];
const PDF_MIME = "application/pdf";

export default {
  name: "ImageInput",
  components: {
    Button,
    FormLabel,
  },
  props: {
    /**
     * ラベルテキスト
     * @type {String}
     * @required
     */
    label: {
      type: String,
      default: "画像アップロード",
    },
    /**
     * 必須項目かどうか
     * @type {Boolean}
     * @default false
     */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * アップロード済み画像データ（v-model用）
     * @type {Array}
     * @default []
     */
    value: {
      type: Array,
      default: () => [],
    },
    /**
     * 最大ファイルサイズ（MB）
     * @type {Number}
     * @default 5
     */
    maxSizeMB: {
      type: Number,
      default: MAX_FILE_SIZE_MB,
    },
  },
  data() {
    return {
      uploadedFiles: [], // アップロード済み画像（v-modelから受け取ったもの）
      newFiles: [], // 新規で追加されたファイル
      removedFileIds: [], // 削除されたアップロード済み画像のID
      isDragOver: false, // ドラッグオーバー状態
      errorMessage: "", // エラーメッセージ
      fileIdCounter: 0, // ファイルID生成用カウンター
      acceptTypes: ALLOWED_MIME_TYPES, // 受け入れ可能なファイルタイプ
    };
  },
  computed: {
    /**
     * 最大ファイルサイズ（バイト単位）
     */
    maxFileSize() {
      return this.maxSizeMB * 1024 * 1024;
    },

    /**
     * 表示用のファイル一覧（アップロード済み+新規）
     */
    selectedFiles() {
      return [...this.uploadedFiles, ...this.newFiles];
    },

    /**
     * アップロード完了済みのファイル一覧
     */
    completedFiles() {
      return this.uploadedFiles;
    },

    /**
     * 未完了（新規追加）のファイル一覧
     */
    pendingFiles() {
      return this.newFiles;
    },
  },
  watch: {
    /**
     * v-modelからの値を監視
     * 外部から渡されたアップロード済み画像を内部状態に反映
     */
    value: {
      handler(newValue) {
        if (!newValue || newValue.length === 0) {
          this.uploadedFiles = [];
          return;
        }

        // 外部から渡されたアップロード済み画像をuploadedFilesに変換
        this.uploadedFiles = newValue.map((item, index) => ({
          id: item.id || `uploaded-${index}-${Date.now()}`,
          name: item.name || `image-${index}`,
          status: "completed",
          previewUrl: item.previewUrl || item.url || "",
          uploadedData: item.uploadedData || item,
        }));
        // v-modelが更新されたら、削除マークをリセット
        this.removedFileIds = [];
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * ファイル選択ダイアログを開く
     */
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    /**
     * ファイル選択イベントハンドラ
     * @param {Event} event - ファイル選択イベント
     */
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
      event.target.value = ""; // 同じファイルを再選択可能にする
    },

    /**
     * ドロップイベントハンドラ
     * @param {DragEvent} event - ドロップイベント
     */
    handleDrop(event) {
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },

    /**
     * ドラッグオーバーイベントハンドラ
     */
    handleDragOver() {
      this.isDragOver = true;
    },

    /**
     * ドラッグリーブイベントハンドラ
     */
    handleDragLeave() {
      this.isDragOver = false;
    },

    /**
     * ファイルのクリーンアップ処理
     * アップロードのキャンセルとプレビューURLの解放
     * @param {Object} file - クリーンアップ対象のファイル
     */
    cleanupFile(file) {
      // プレビューURLを解放
      if (file.previewUrl && file.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(file.previewUrl);
      }
    },

    /**
     * ファイルをリストに追加
     * ファイルタイプとサイズのバリデーションを実行
     * @param {File[]} files - 追加するファイルの配列
     */
    addFiles(files) {
      this.errorMessage = "";

      // バリデーション: ファイルタイプとサイズをチェック
      const validFiles = files.filter(file => {
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
          this.errorMessage = `画像（PNG、JPG、JPEG、GIF）および PDF のみアップロードできます。`;
          return false;
        }
        if (file.size > this.maxFileSize) {
          this.errorMessage = `ファイル「${
            file.name
          }」のサイズが${this.convertBytes(this.maxFileSize)}を超えています。`;
          return false;
        }
        return true;
      });

      // 有効なファイルを追加
      if (validFiles.length > 0) {
        const newFiles = validFiles.map(file => ({
          id: `file-${this.fileIdCounter++}-${Date.now()}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
          status: "pending",
          previewUrl: URL.createObjectURL(file),
        }));
        this.newFiles = [...this.newFiles, ...newFiles];
        // 変更を親に通知
        this.emitChange();
      }
    },

    /**
     * IDをファイル削除リストに追加
     * @param {String} fileId - 削除するファイルのID
     */
    removeFileById(fileId) {
      // アップロード済み画像から削除
      const uploadedIndex = this.uploadedFiles.findIndex(f => f.id === fileId);
      if (uploadedIndex !== -1) {
        const file = this.uploadedFiles[uploadedIndex];
        if (file && file.previewUrl && file.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(file.previewUrl);
        }
        this.uploadedFiles.splice(uploadedIndex, 1);
        // 削除されたIDを記録
        if (file.id && !file.id.startsWith("file-")) {
          this.removedFileIds.push(file.id);
        }
        this.emitChange();
        return;
      }

      // 新規ファイルから削除
      const newIndex = this.newFiles.findIndex(f => f.id === fileId);
      if (newIndex !== -1) {
        const file = this.newFiles[newIndex];
        if (file && file.previewUrl && file.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(file.previewUrl);
        }
        this.newFiles.splice(newIndex, 1);
        this.emitChange();
      }

      this.errorMessage = "";
    },

    /**
     * 変更を親に通知
     */
    emitChange() {
      this.$emit("change", {
        uploadedFiles: this.uploadedFiles,
        newFiles: this.newFiles,
        removedFileIds: this.removedFileIds,
      });
    },

    /**
     * ファイルがPDFかどうか
     * @param {Object} file - ファイルオブジェクト（type または name を持つ）
     * @returns {boolean}
     */
    isPdf(file) {
      if (!file) return false;
      if (file.type === PDF_MIME) return true;
      const name = file.name || "";
      return name.toLowerCase().endsWith(".pdf");
    },

    /**
     * バイト数を人間が読みやすい形式に変換
     * @param {Number} valueInBytes - バイト数
     * @returns {String} 変換後の文字列（例: "5 MB"）
     */
    convertBytes(valueInBytes) {
      const units = ["B", "KB", "MB", "GB", "TB"];
      let size = valueInBytes;
      let unitIndex = 0;

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }

      return `${Math.round(size)} ${units[unitIndex]}`;
    },
  },

  /**
   * コンポーネント破棄前の処理
   * メモリリークを防ぐため、全ファイルをクリーンアップ
   */
  beforeDestroy() {
    const allFiles = [...this.uploadedFiles, ...this.newFiles];
    allFiles.forEach(file => {
      if (file && file.previewUrl && file.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

// ============================================
// メインコンテナ
// ============================================
.image-uploader-container {
  display: flex;
  max-width: 1440px;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.image-uploader-main-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
}

.upload-form-label {
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;
}

// ============================================
// 左側セクション: ドロップゾーン
// ============================================
.left-section {
  display: flex;
  min-width: 320px;
  max-width: 440px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
}

.drop-zone-wrapper {
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  padding: 8px;
}

.drop-zone {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  padding: 24px 89px;
  border-radius: 6px;
  border: 1px dashed $aisea-blue;
  transition: all 0.3s ease;

  .drop-zone-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;

    .upload-icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      color: $aisea-blue;
    }

    .file-upload-button {
      all: unset;
    }
  }

  .drop-zone-text {
    color: #626262;
    text-align: center;
    font-family: "Hiragino Sans", -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22.4px;
  }
}

// ============================================
// 右側セクション: ファイルリストとアクションボタン
// ============================================
.right-section {
  max-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  align-self: stretch;
  flex: 1;
  gap: 16px;
  overflow: auto;
}

// ファイルリストコンテナ
.files-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 16px;
  flex: 1 0 0;
  width: 100%;
}

// 空状態表示
.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  .empty-state-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }

  .format-text {
    color: $aisea-dark-gray;
    font-family: "Hiragino Sans", -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 22.4px;
    text-align: center;
  }
}

// アップロード済みファイルセクション
.completed-files-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
}

// 未完了ファイルセクション
.pending-files-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
}

.upload-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

// ============================================
// アップロードカード（ファイル項目）
// ============================================
.upload-card {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  border: 1px solid #e7e7e7;
  background: #fff;
  padding: 12px 16px;
  width: 100%;
  min-width: 0;
  max-width: 500px;
  transition: box-shadow 0.2s, border-color 0.2s;

  // アップロード済みファイル用のスタイル
  &.completed {
    border: 2px solid $aisea-green; // 緑の枠線
    background: #f1f8f4; // 薄い緑の背景
  }
}

.file-preview {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e7e7e7;
  background: #fafafa;

  .preview-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .preview-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bdbdbd;
  }

  .preview-pdf {
    background: #e3f2fd;
    color: #1976d2;
  }

  .preview-pdf-text {
    font-size: 10px;
    font-weight: 600;
  }
}

.upload-card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
}

.upload-card-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.upload-card-row-full {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  align-self: stretch;
  width: 100%;
}

.file-name-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.file-name {
  color: #4f4f4f;
  font-family: Inter, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0;
  word-break: break-all;
}

.upload-card-actions {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.action-icon {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  &.cancel-icon {
    color: $aisea-red;

    ::v-deep .vs-icon {
      color: $aisea-red;
    }
  }
}

// ============================================
// エラーメッセージ
// ============================================
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fff0f2;
  border: 2px solid #f44336;
  border-radius: 8px;
  color: #f44336;
  font-size: 14px;
  font-weight: 700;
  margin-top: 16px;
}
</style>
